import { StateSetter } from 'react';

import { actions, getStatusConfig, SuggestionWithUser } from 'entities';

import { Box } from 'components/Blocks';
import { ChevronUpIcon } from 'components/Icons';

import { useUser } from 'hooks';

import { cn } from 'styles';
import { toUniqueList } from 'utils/array';

interface UpvoteSuggestionButtonProps {
  suggestion: SuggestionWithUser;
  className?: string;

  updateSuggestion?: StateSetter<SuggestionWithUser>;
}

export function UpvoteSuggestionButton({
  suggestion,
  className,
  updateSuggestion,
}: UpvoteSuggestionButtonProps) {
  const loggedUser = useUser();

  const { status, user, upVotes } = suggestion;

  const { canVote } = getStatusConfig('userSuggestion', status);

  const fullUpVotes = toUniqueList([...upVotes, user.id]);

  const canUpvote = canVote && user.id !== loggedUser.id && !!updateSuggestion;

  const upvote = actions.feedback.toggleSuggestionUpvote.use({
    block: !canUpvote,

    params: { id: suggestion.id },

    onError: () =>
      updateSuggestion?.((old) => ({
        ...old,
        upVotes: old.upVotes.filter((id) => id !== loggedUser.id),
      })),
  });

  const optimisticClick = () => {
    updateSuggestion?.((old) => ({
      ...old,
      upVotes: old.upVotes.includes(loggedUser.id)
        ? old.upVotes.filter((id) => id !== loggedUser.id)
        : toUniqueList([...old.upVotes, loggedUser.id]),
    }));

    upvote.onClick();
  };

  return (
    <button
      disabled={!canUpvote}
      onClick={optimisticClick}
      className={cn(
        'flex flex-col items-center justify-center rounded-md px-2 outline-none',
        canUpvote && 'hover:outline-2 hover:outline-success',
        className,
      )}
    >
      <Box border="circle">
        <ChevronUpIcon color={fullUpVotes.includes(loggedUser.id) ? 'success' : undefined} />
      </Box>

      <span
        className={cn('text-sm font-medium', fullUpVotes.includes(loggedUser.id) && 'text-success')}
      >
        {fullUpVotes.length}
      </span>
    </button>
  );
}
